/**
 * BranchDialog Component
 *
 * Main dialog component for creating and editing branches.
 * Implements the complete form structure from the implementation plan.
 */

import { CountrySelector } from '@/components/common/CountrySelector';
import { FormFieldWrapper } from '@/components/forms/FormFieldWrapper';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getCantonOptions,
  getLanguageOptions,
} from '@/constants/branchOptions';
import { useBranchForm } from '@/hooks/useBranch';
import {
  useLocalBranchContacts,
  useLocalUpdateBranchContact,
} from '@/hooks/useLocalBranchContact';
import { useFormsTranslation } from '@/hooks/useTranslation';
import type { BranchDialogProps } from '@/types/branch.types';
import type {
  BranchContact,
  BranchContactFormData,
} from '@/types/branchContact.types';
import {
  Building,
  Clock,
  Edit,
  Mail,
  MapPin,
  Phone,
  Save,
  User,
  Users,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import BranchContactDialog from './BranchContactDialog';
import OpeningHoursSelector from './OpeningHoursSelector';

const BranchDialog: React.FC<BranchDialogProps> = ({
  open,
  onOpenChange,
  branch,
  onSave,
  isLoading = false,
}) => {
  const { t: tForms } = useFormsTranslation();
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<BranchContact | null>(
    null
  );

  // Use the updated useBranchForm hook following ContactPartnerDialog pattern
  const { form, handleSubmit, isEditing } = useBranchForm({
    branch,
    onSave,
    isOpen: open,
  });

  // Fetch contacts for this branch (only when editing)
  const { data: contactsData } = useLocalBranchContacts(
    isEditing && branch ? branch.id : undefined
  );
  const updateContactMutation = useLocalUpdateBranchContact();

  const branchContacts = Array.isArray(contactsData?.data)
    ? contactsData.data.filter((contact: BranchContact) => {
        // Handle both old and new data formats
        if (branch && contact.branchIds && Array.isArray(contact.branchIds)) {
          return contact.branchIds.includes(branch.id);
        }
        return false;
      })
    : [];

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  const handleEditContact = (contact: BranchContact) => {
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const handleSaveContact = async (data: BranchContactFormData) => {
    if (editingContact) {
      await updateContactMutation.mutateAsync({
        id: editingContact.id,
        data,
      });
    }
    setContactDialogOpen(false);
    setEditingContact(null);
  };

  const cantonOptions = getCantonOptions(tForms);
  const languageOptions = getLanguageOptions(tForms);
  const selectedCountry = form.watch('country');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[900px] max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Building className='h-5 w-5' />
            {isEditing
              ? tForms('branch.dialog.title.edit')
              : tForms('branch.dialog.title.create')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? tForms('branch.dialog.description.edit')
              : tForms('branch.dialog.description.create')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className='max-h-[calc(90vh-120px)] pr-4'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className='space-y-6'
            >
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-lg'>
                    <Building className='h-4 w-4' />
                    {tForms('branch.dialog.sections.generalInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* Language */}
                    <FormField
                      control={form.control}
                      name='language'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branch.dialog.labels.language')}
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={isLoading}
                          >
                            <FormControl>
                              <SelectTrigger className='min-h-[44px] w-full'>
                                <SelectValue
                                  placeholder={tForms(
                                    'branch.dialog.placeholders.language'
                                  )}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {languageOptions.map(option => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Branch Name */}
                    <FormField
                      control={form.control}
                      name='name'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branch.dialog.labels.name')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={tForms(
                                'branch.dialog.placeholders.name'
                              )}
                              disabled={isLoading}
                              className='w-full min-h-[44px]'
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Address Information */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-lg'>
                    <MapPin className='h-4 w-4' />
                    {tForms('branch.dialog.sections.addressInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-6'>
                  {/* Row 1: Country & Canton (only for Switzerland) */}
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
                    <FormField
                      control={form.control}
                      name='country'
                      render={({ field, fieldState }) => (
                        <FormFieldWrapper
                          label={tForms('branch.dialog.labels.country')}
                          name='country'
                          required
                          error={fieldState.error?.message}
                        >
                          <CountrySelector
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder={tForms(
                              'branch.dialog.placeholders.country'
                            )}
                            disabled={isLoading}
                            error={fieldState.error?.message}
                            required
                            name='country'
                          />
                        </FormFieldWrapper>
                      )}
                    />

                    {/* Canton (only for Switzerland) */}
                    {selectedCountry === 'CH' && (
                      <FormField
                        control={form.control}
                        name='canton'
                        render={({ field, fieldState }) => (
                          <FormFieldWrapper
                            label={tForms('branch.dialog.labels.canton')}
                            name='canton'
                            required
                            error={fieldState.error?.message}
                          >
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || ''}
                              disabled={isLoading}
                            >
                              <SelectTrigger className='min-h-[44px] w-full'>
                                <SelectValue
                                  placeholder={tForms(
                                    'branch.dialog.placeholders.canton'
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {cantonOptions.map(option => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormFieldWrapper>
                        )}
                      />
                    )}
                  </div>

                  {/* Row 2: Street & House Number */}
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
                    <FormField
                      control={form.control}
                      name='street'
                      render={({ field, fieldState }) => (
                        <FormFieldWrapper
                          label={tForms('branch.dialog.labels.street')}
                          name='street'
                          required
                          error={fieldState.error?.message}
                        >
                          <Input
                            {...field}
                            id='street'
                            type='text'
                            placeholder={tForms(
                              'branch.dialog.placeholders.street'
                            )}
                            className='w-full min-h-[44px]'
                            disabled={isLoading}
                            autoComplete='address-line1'
                          />
                        </FormFieldWrapper>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='number'
                      render={({ field, fieldState }) => (
                        <FormFieldWrapper
                          label={tForms('branch.dialog.labels.number')}
                          name='number'
                          required
                          error={fieldState.error?.message}
                        >
                          <Input
                            {...field}
                            id='number'
                            type='text'
                            placeholder={tForms(
                              'branch.dialog.placeholders.number'
                            )}
                            className='w-full min-h-[44px]'
                            disabled={isLoading}
                            autoComplete='address-line2'
                          />
                        </FormFieldWrapper>
                      )}
                    />
                  </div>

                  {/* Row 3: ZIP Code & City */}
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
                    <FormField
                      control={form.control}
                      name='zipCode'
                      render={({ field, fieldState }) => (
                        <FormFieldWrapper
                          label={tForms('branch.dialog.labels.zipCode')}
                          name='zipCode'
                          required
                          error={fieldState.error?.message}
                        >
                          <Input
                            {...field}
                            id='zipCode'
                            type='text'
                            placeholder={tForms(
                              'branch.dialog.placeholders.zipCode'
                            )}
                            className='w-full min-h-[44px]'
                            disabled={isLoading}
                            autoComplete='postal-code'
                          />
                        </FormFieldWrapper>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='city'
                      render={({ field, fieldState }) => (
                        <FormFieldWrapper
                          label={tForms('branch.dialog.labels.city')}
                          name='city'
                          required
                          error={fieldState.error?.message}
                        >
                          <Input
                            {...field}
                            id='city'
                            type='text'
                            placeholder={tForms(
                              'branch.dialog.placeholders.city'
                            )}
                            className='w-full min-h-[44px]'
                            disabled={isLoading}
                            autoComplete='address-level2'
                          />
                        </FormFieldWrapper>
                      )}
                    />
                  </div>

                  {/* Optional: Postal Code field if needed */}
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
                    <FormField
                      control={form.control}
                      name='postalCode'
                      render={({ field, fieldState }) => (
                        <FormFieldWrapper
                          label={tForms('branch.dialog.labels.poBox')}
                          name='postalCode'
                          error={fieldState.error?.message}
                        >
                          <Input
                            {...field}
                            id='postalCode'
                            type='text'
                            placeholder={tForms(
                              'branch.dialog.placeholders.poBox'
                            )}
                            className='w-full min-h-[44px]'
                            disabled={isLoading}
                          />
                        </FormFieldWrapper>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Opening Hours */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-lg'>
                    <Clock className='h-4 w-4' />
                    {tForms('branch.dialog.sections.operatingHours')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name='openingHours'
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <OpeningHoursSelector
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Branch Contacts Section - Only show when editing */}
              {isEditing && branch && (
                <Card>
                  <CardHeader>
                    <CardTitle className='flex items-center gap-2 text-lg'>
                      <Users className='h-4 w-4' />
                      {tForms('branch.dialog.sections.contacts')}
                      <Badge variant='secondary' className='ml-2'>
                        {branchContacts.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {branchContacts.length === 0 ? (
                      <div className='text-center py-8 text-muted-foreground'>
                        <Users className='h-12 w-12 mx-auto mb-3 opacity-50' />
                        <p className='text-sm'>
                          {tForms('branch.dialog.noContacts')}
                        </p>
                        <p className='text-xs mt-1'>
                          {tForms('branch.dialog.noContactsHint')}
                        </p>
                      </div>
                    ) : (
                      <div className='space-y-3'>
                        {branchContacts.map((contact: BranchContact) => (
                          <div
                            key={contact.id}
                            className='flex items-start justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors'
                          >
                            <div className='flex items-start gap-3 flex-1'>
                              <div className='bg-primary/10 p-2 rounded-full'>
                                <User className='h-4 w-4 text-primary' />
                              </div>
                              <div className='flex-1 space-y-1'>
                                <div className='flex items-center gap-2'>
                                  <h4 className='font-medium text-sm'>
                                    {contact.firstName} {contact.lastName}
                                  </h4>
                                  {contact.isRepresentative && (
                                    <Badge
                                      variant='default'
                                      className='text-xs'
                                    >
                                      {tForms(
                                        'branchContact.dialog.labels.representative'
                                      )}
                                    </Badge>
                                  )}
                                </div>
                                <p className='text-xs text-muted-foreground'>
                                  {contact.function}
                                </p>
                                <div className='flex flex-wrap gap-3 mt-2'>
                                  <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                                    <Mail className='h-3 w-3' />
                                    {contact.email}
                                  </div>
                                  <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                                    <Phone className='h-3 w-3' />
                                    {contact.phone}
                                  </div>
                                  {contact.mobile && (
                                    <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                                      <Phone className='h-3 w-3' />
                                      {contact.mobile}
                                    </div>
                                  )}
                                </div>
                                {contact.branchIds.length > 1 && (
                                  <div className='mt-2'>
                                    <Badge
                                      variant='outline'
                                      className='text-xs'
                                    >
                                      {tForms(
                                        'branch.dialog.assignedToMultipleBranches',
                                        {
                                          count: contact.branchIds.length,
                                        }
                                      )}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => handleEditContact(contact)}
                              className='flex items-center gap-1'
                            >
                              <Edit className='h-3 w-3' />
                              {tForms('branch.dialog.actions.editContact')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className='flex justify-end gap-3 pt-4 border-t'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleCancel}
                  disabled={isLoading}
                  className='flex items-center gap-2'
                >
                  <X className='h-4 w-4' />
                  {tForms('branch.dialog.actions.cancel')}
                </Button>
                <Button
                  type='submit'
                  disabled={isLoading}
                  className='flex items-center gap-2'
                >
                  <Save className='h-4 w-4' />
                  {isLoading
                    ? `${tForms('branch.dialog.actions.save')}...`
                    : tForms('branch.dialog.actions.save')}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>

      {/* Contact Edit Dialog */}
      <BranchContactDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        branchContact={editingContact || undefined}
        onSave={handleSaveContact}
        isLoading={updateContactMutation.isPending}
      />
    </Dialog>
  );
};

export default BranchDialog;
